import { StatusVerwaltung } from '@bo-client/shared/config.model';
import { RequestEntityType } from '@bo-client/shared/request-entity.util';

/** Module mit Ausblend-Konfig; Schlüssel wie die Unterobjekte der Kategorie „Status-Verwaltung“. */
export type StatusModule = keyof StatusVerwaltung;

/**
 * Alle Statuskataloge. `orders` ist der Auftragsstatus je Sparte eines Hausanschlusses (`Order.state`),
 * `orderRequests` der Status der Anfrage (`OrderRequest.state`).
 */
export type StatusCatalogKey = StatusModule | 'orders';

/** Default-Definition eines Status; Bezeichnung und Farbe gelten, solange der Mandant nichts anderes hinterlegt. */
export interface StatusDefinition {
  value: string;
  defaultLabel: string;
  defaultColor?: string;
}

/** Status, wie ihn Listen, Filter und Auswahlen anzeigen. */
export interface StatusType {
  value: string;
  displayName?: string;
  cssColor?: string;
}

/** Option für die Statusauswahl; `disabled` markiert den aktuellen, ausgeblendeten Status. */
export interface SelectableStatus extends StatusType {
  disabled?: boolean;
}

/** Statuskatalog je Backend-Entity (z. B. für Workflow-Regeln); Entities ohne Status fehlen. */
export const STATUS_CATALOG_BY_REQUEST_ENTITY: Partial<Record<RequestEntityType, StatusCatalogKey>> = {
  'de.tsg.vs.platform.features.order.model.OrderRequest': 'orderRequests',
  'de.tsg.vs.platform.features.feeder_contracts.model.FeederContract': 'feederContract',
  'de.tsg.vs.platform.features.grid_registrations.model.GridRegistration': 'gridRegistration',
  'de.tsg.vs.platform.features.commissioning.model.CommissioningRequest': 'commissioning',
  'de.tsg.vs.platform.features.connection_change.model.ConnectionChangeRequest': 'connectionChangeRequest'
};

/** Präfixe der `referenceId` (`<Präfix>-<id>`) je Statuskatalog. */
const STATUS_CATALOG_BY_REFERENCE_PREFIX: Record<string, StatusCatalogKey> = {
  GridRegistration: 'gridRegistration',
  CommissioningRequest: 'commissioning',
  FeederContract: 'feederContract',
  OrderRequest: 'orderRequests',
  ConnectionChange: 'connectionChangeRequest',
  InstallerPass: 'installerPassList'
};

/** Statuskatalog eines Vorgangs anhand seiner `referenceId`; unbekannt → `undefined`. */
export function statusCatalogForReferenceId(
  referenceId: string | null | undefined
): StatusCatalogKey | undefined {
  const prefix = referenceId?.substring(0, referenceId.lastIndexOf('-'));
  return prefix ? STATUS_CATALOG_BY_REFERENCE_PREFIX[prefix] : undefined;
}

/** Dialogdaten der Aktivitätsdetails: die Aktivität plus der Statuskatalog ihres Vorgangs. */
export type ActivityWithStatusModule<T> = T & { statusModule?: StatusCatalogKey };
